import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as mutationService from '../../services/mutationService';
import { Mutation } from '../../types/mutation';
import { MutationStatus, MutationResponse, MutationCreate, MutationConfirm } from '../../services/mutationService';

interface MutationState {
  mutations: MutationResponse[];
  selectedMutation: MutationResponse | null;
  loading: boolean;
  error: string | null;
}

const initialState: MutationState = {
  mutations: [],
  selectedMutation: null,
  loading: false,
  error: null,
};

export const fetchMutationsAsync = createAsyncThunk(
  'mutations/fetchMutations',
  async (params?: {
    from_branch_id?: number;
    to_branch_id?: number;
    status?: MutationStatus;
    page?: number;
    page_size?: number;
  }) => {
    const response = await mutationService.fetchMutations(params);
    return response;
  }
);

export const getMutationByIdAsync = createAsyncThunk(
  'mutations/getMutationById',
  async (id: number) => {
    const response = await mutationService.getMutationById(id);
    return response;
  }
);

export const createMutationAsync = createAsyncThunk(
  'mutations/createMutation',
  async (mutation: MutationCreate, { rejectWithValue }) => {
    try {
      const response = await mutationService.createMutation(mutation);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to create mutation');
    }
  }
);

export const confirmMutationAsync = createAsyncThunk(
  'mutations/confirmMutation',
  async ({ id, data }: { id: number; data?: MutationConfirm }, { rejectWithValue }) => {
    try {
      const response = await mutationService.confirmMutation(id, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to confirm mutation');
    }
  }
);

export const cancelMutationAsync = createAsyncThunk(
  'mutations/cancelMutation',
  async ({ id, reason }: { id: number; reason: string }, { rejectWithValue }) => {
    try {
      const response = await mutationService.cancelMutation(id, reason);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to cancel mutation');
    }
  }
);

const mutationSlice = createSlice({
  name: 'mutations',
  initialState,
  reducers: {
    clearSelectedMutation(state) {
      state.selectedMutation = null;
    },
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Mutations
      .addCase(fetchMutationsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMutationsAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.mutations = action.payload;
      })
      .addCase(fetchMutationsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch mutations';
      })
      // Get Mutation by ID
      .addCase(getMutationByIdAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(getMutationByIdAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedMutation = action.payload;
      })
      .addCase(getMutationByIdAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Mutation not found';
      })
      // Create Mutation
      .addCase(createMutationAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createMutationAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.mutations.push(action.payload);
      })
      .addCase(createMutationAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Failed to create mutation';
      })
      // Confirm Mutation
      .addCase(confirmMutationAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(confirmMutationAsync.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.mutations.findIndex(m => m.id === action.payload.id);
        if (index !== -1) {
          state.mutations[index] = action.payload;
        }
        if (state.selectedMutation?.id === action.payload.id) {
          state.selectedMutation = action.payload;
        }
      })
      .addCase(confirmMutationAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Failed to confirm mutation';
      })
      // Cancel Mutation
      .addCase(cancelMutationAsync.fulfilled, (state, action) => {
        const index = state.mutations.findIndex(m => m.id === action.payload.id);
        if (index !== -1) {
          state.mutations[index] = action.payload;
        }
      });
  },
});

export const { clearSelectedMutation, clearError } = mutationSlice.actions;

// Selectors
export const selectMutations = (state: any) => state.mutations.mutations;
export const selectSelectedMutation = (state: any) => state.mutations.selectedMutation;
export const selectLoading = (state: any) => state.mutations.loading;
export const selectError = (state: any) => state.mutations.error;

export default mutationSlice.reducer;